






































































































































































import { Component, Prop, Watch } from 'vue-property-decorator';
import MixinDialog from '@/plugins/podolib/services/helper/mixins/dialog';
import { getCountries } from '@/plugins/i18n';

import { TForm } from '@/plugins/podolib/lib/types';
import { TEntity } from '@/plugins/podolib/services/entities/Entity';
import { Doctor, Office } from '@/plugins/podolib/services/doctor';
import { ValidationProvider } from 'vee-validate/dist/types/components/Provider';
import { Severity } from '@sentry/types';

type TFields = {
  officeName: {
    value: Office['officeName'];
  };
  address: {
    street: {
      value: Office['address']['street'];
    };
    zipCode: {
      value: Office['address']['zipCode'];
    };
    city: {
      value: Office['address']['city'];
    };
    country: {
      value: Office['address']['country'];
    };
    state: {
      value: Office['address']['state'];
    };
  };
  phones: {
    value: Office['phones'];
  };
};

type TMode = 'add' | 'edit';

interface IAccountOfficeAddOrEdit extends MixinDialog {
  mode: TMode;
  form: TForm<TFields>;
}

@Component<IAccountOfficeAddOrEdit>({
  mounted() {
    if (this.$route.params.addOffice && this.automatedOpen && this.mode === 'add') {
      this.open();
    }
  },
})
export default class AccountOfficeAddOrEdit extends MixinDialog {
  $refs!: {
    observer: InstanceType<typeof ValidationProvider>;
    form: any;
    comboPhones: HTMLInputElement;
  };

  @Prop({ required: true }) mode!: TMode;
  @Prop({ required: true }) doctor!: TEntity<Doctor>;
  @Prop() officeId!: Office['_id'] | null;

  form: TForm<TFields> = {
    fields: {
      officeName: {
        value: '',
      },
      address: {
        street: {
          value: '',
        },
        state: {
          value: '',
        },
        city: {
          value: '',
        },
        country: {
          value: '',
        },
        zipCode: {
          value: '',
        },
      },
      phones: {
        value: [],
      },
    },
    pending: false,
    error: null,
    success: false,
  };
  phoneCreator: string = '';

  get offices(): Office[] {
    return this.doctor.offices.items;
  }

  get office(): Office | null {
    if (!this.officeId) return null;
    return this.offices.find((x) => x._id === this.officeId)!;
  }

  get countries() {
    return getCountries({ excludeNoDial: true });
  }

  @Watch('dialog')
  onDialogChanged(state: boolean) {
    if (state) {
      if (this.mode === 'edit' && this.office) {
        // Populate form
        this.form.fields.officeName.value = this.office.officeName;
        this.form.fields.address.street.value = this.office.address.street;
        this.form.fields.address.city.value = this.office.address.city;
        this.form.fields.address.zipCode.value = this.office.address.zipCode;
        this.form.fields.address.country.value = this.office.address.country;
        this.form.fields.address.state.value = this.office.address.state;
        this.form.fields.phones.value = this.office.phones || [];
      }

      // Reset flags
      this.form.success = false;
      this.form.error = null;
    }
  }

  @Watch('form.fields', { deep: true })
  onFormFieldsDeeplyChanged(newFormFields: TFields) {
    // To avoid flash css switch
    if (!this.dialog) return;
    // Reset flags
    this.form.success = false;
    this.form.error = null;
  }

  cleanValue(value: any, defaultValue?: any): any {
    if (typeof value === 'undefined') {
      return defaultValue || null;
    } else {
      return value;
    }
  }

  addPhone() {
    this.form.fields.phones.value.push(this.phoneCreator);
    this.phoneCreator = '';
    this.$refs.comboPhones.blur();
  }

  async save() {
    this.mode === 'add' ? this.add() : this.edit();
  }

  async add() {
    // Avoid duplication of the process
    if (this.form.pending) return;

    // New payload
    const _item: any = {
      officeName: this.cleanValue(this.form.fields.officeName.value),
      address: {
        street: this.cleanValue(this.form.fields.address.street.value),
        country: this.cleanValue(this.form.fields.address.country.value),
        zipCode: this.cleanValue(this.form.fields.address.zipCode.value),
        city: this.cleanValue(this.form.fields.address.city.value),
        state: this.cleanValue(this.form.fields.address.state.value),
      },
      phones: this.form.fields.phones.value,
    };

    try {
      // @ts-ignore
      const isValid = await this.$refs.observer.validate();
      if (!isValid) return;
      // Reset flags
      this.form.success = false;
      this.form.error = null;
      // Set 'pending' flag
      this.form.pending = true;
      // Try to patch the station ...
      await this.$pdc.repository!.patchRessource({
        ressource: 'doctors',
        params: {
          _id: this.doctor._id,
        },
        data: {
          offices: {
            ...this.doctor.offices,
            items: [...this.offices, _item],
          },
        },
      });
      // Analytics
      this.$pdc.analytics?.actions.trackSuccess('User OfficeCreated', _item);

      this.form.success = true;
    } catch (e) {
      // Analytics
      this.$pdc.analytics?.actions.trackFailure('User OfficeCreated', e, _item);
      // Set 'error' flag
      this.form.error = e;
      // Sentry
      this.$sentry.captureMessage(e.message, Severity.Error);
    } finally {
      // Reset 'pending' flag
      this.form.pending = false;
    }
  }

  async edit() {
    // Avoid duplication of the process
    if (this.form.pending) return;

    const data = {
      offices: {
        ...this.doctor.offices,
        items: this.offices.map((x) => {
          if (x._id === this.officeId) {
            return {
              ...x,
              officeName: this.cleanValue(this.form.fields.officeName.value),
              address: {
                street: this.cleanValue(this.form.fields.address.street.value),
                country: this.cleanValue(this.form.fields.address.country.value),
                zipCode: this.cleanValue(this.form.fields.address.zipCode.value),
                city: this.cleanValue(this.form.fields.address.city.value),
                state: this.cleanValue(this.form.fields.address.state.value),
              },
              phones: this.form.fields.phones.value,
            };
          } else {
            return x;
          }
        }),
      },
    };

    const previous = this.offices.find((x) => x._id === this.officeId);

    try {
      // @ts-ignore
      const isValid = await this.$refs.observer.validate();
      if (!isValid) return;
      // Reset flags
      this.form.success = false;
      this.form.error = null;
      // Set 'pending' flag
      this.form.pending = true;
      // Try to patch the station ...
      await this.$pdc.repository!.patchRessource({
        ressource: 'doctors',
        params: {
          _id: this.doctor._id,
        },
        data,
      });
      // Analytics
      this.$pdc.analytics?.actions.trackSuccess(
        'User OfficeEdited',
        {
          ...data.offices.items.find((x) => x._id === this.officeId),
        },
        previous,
      );

      this.form.success = true;
    } catch (e) {
      // Analytics
      this.$pdc.analytics?.actions.trackFailure(
        'User OfficeEdited',
        e,
        {
          ...data.offices.items.find((x) => x._id === this.officeId),
        },
        previous,
      );
      // Set 'error' flag
      this.form.error = e;
      // Sentry
      this.$sentry.captureMessage(e.message, Severity.Error);
    } finally {
      // Reset 'pending' flag
      this.form.pending = false;
    }
  }
}
